import { jsPDF } from "jspdf";

const PAGE_MARGIN = 10;
const FOOTER_SPACE = 10;
const CELL_PADDING = 2;
const FONT_SIZE = 6;
const LINE_HEIGHT = 3;
const COLORS = {
  ink: [35, 28, 46],
  muted: [107, 97, 118],
  accent: [112, 70, 151],
  header: [54, 38, 71],
  border: [225, 220, 231],
  stripe: [247, 245, 250],
  white: [255, 255, 255],
};

export function exportWorkspacePdf({ title, category, headers, rows, filename }) {
  const doc = new jsPDF({
    orientation: headers.length > 5 ? "landscape" : "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableWidth = pageWidth - PAGE_MARGIN * 2;
  const columnWidth = tableWidth / headers.length;
  const columnWidths = headers.map(() => columnWidth);
  const tableBottom = pageHeight - PAGE_MARGIN - FOOTER_SPACE;
  const generatedAt = new Date().toLocaleString();

  doc.setProperties({
    title: `${title} - ${category}`,
    subject: "Admin workspace table export",
    creator: "Conveyor Belt Admin",
  });

  const drawPageHeader = () => {
    doc.setFillColor(...COLORS.header);
    doc.rect(0, 0, pageWidth, 25, "F");
    doc.setFillColor(...COLORS.accent);
    doc.rect(0, 25, pageWidth, 1.5, "F");
    doc.setTextColor(...COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(title, PAGE_MARGIN, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      `${category} scope  |  ${rows.length} records  |  Generated ${generatedAt}`,
      PAGE_MARGIN,
      19,
    );
  };

  const drawTableHeader = (startY) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    const wrappedHeaders = headers.map((header, index) =>
      doc.splitTextToSize(String(header), columnWidths[index] - CELL_PADDING * 2),
    );
    const headerLines = Math.max(...wrappedHeaders.map((lines) => lines.length));
    const headerHeight = Math.max(9, headerLines * LINE_HEIGHT + 4);
    let x = PAGE_MARGIN;

    headers.forEach((_, index) => {
      doc.setFillColor(...COLORS.header);
      doc.setDrawColor(...COLORS.header);
      doc.rect(x, startY, columnWidths[index], headerHeight, "FD");
      doc.setTextColor(...COLORS.white);
      doc.text(
        wrappedHeaders[index],
        x + CELL_PADDING,
        startY + 4,
        { lineHeightFactor: 1.15 },
      );
      x += columnWidths[index];
    });
    return startY + headerHeight;
  };

  const startPage = () => {
    drawPageHeader();
    return drawTableHeader(32);
  };

  let y = startPage();
  if (rows.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.muted);
    doc.text("No records match the current search or status filter.", PAGE_MARGIN, y + 8);
  }

  rows.forEach((row, rowIndex) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(FONT_SIZE);
    const wrappedCells = row.map((value, index) =>
      doc.splitTextToSize(
        String(value ?? "") || "-",
        columnWidths[index] - CELL_PADDING * 2,
      ),
    );
    const lineCount = Math.max(...wrappedCells.map((lines) => lines.length), 1);
    let lineOffset = 0;

    while (lineOffset < lineCount) {
      const availableLines = Math.floor((tableBottom - y - 3) / LINE_HEIGHT);
      if (availableLines < 1) {
        doc.addPage();
        y = startPage();
        continue;
      }

      const linesInFragment = Math.min(availableLines, lineCount - lineOffset);
      const rowHeight = linesInFragment * LINE_HEIGHT + 3;
      let x = PAGE_MARGIN;

      for (let columnIndex = 0; columnIndex < wrappedCells.length; columnIndex += 1) {
        const lines = wrappedCells[columnIndex];
        doc.setFillColor(
          ...(rowIndex % 2 === 0 ? COLORS.white : COLORS.stripe),
        );
        doc.setDrawColor(...COLORS.border);
        doc.rect(x, y, columnWidths[columnIndex], rowHeight, "FD");
        doc.setTextColor(...COLORS.ink);
        doc.text(
          lines.slice(lineOffset, lineOffset + linesInFragment),
          x + CELL_PADDING,
          y + 3.2,
          { lineHeightFactor: 1.15 },
        );
        x += columnWidths[columnIndex];
      }

      y += rowHeight;
      lineOffset += linesInFragment;
      if (lineOffset < lineCount) {
        doc.addPage();
        y = startPage();
      }
    }
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...COLORS.border);
    doc.line(
      PAGE_MARGIN,
      pageHeight - PAGE_MARGIN - 5,
      pageWidth - PAGE_MARGIN,
      pageHeight - PAGE_MARGIN - 5,
    );
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.muted);
    doc.text("Conveyor Belt Admin", PAGE_MARGIN, pageHeight - PAGE_MARGIN - 1);
    doc.text(
      `Page ${page} of ${pageCount}`,
      pageWidth - PAGE_MARGIN,
      pageHeight - PAGE_MARGIN - 1,
      { align: "right" },
    );
  }

  doc.save(filename);
}
